import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import moment from 'moment-timezone';
import { Icon } from '@famly/mf_data-display_icon';
import { Text } from '@famly/mf_data-display_text';
import { Stack, createStack } from '@famly/mf_layout_stack';
import { Box } from '@famly/mf_layout_box';
import { useBreakpoints } from '@famly/mf_util_breakpoints';
import { limitedData } from 'signin-app/child/check-out/selectors';
import useTypedSelector from 'signin-app/components/hooks/use-typed-selector';
import { showSignedIn } from 'signin-app/group/selectors';
import * as GroupsSelectors from 'signin-app/groups/selectors';
import { PersonImage } from 'signin-app/components/person-image';
import { getWentHome } from 'web-app/react/components/person-button/get-went-home';
import { useCustomWhitelabelColor } from 'signin-app/components/hooks/use-custom-whitelabel-color';
import { Badge } from './badge';
const StyledPersonContainer = styled(createStack({
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    position: 'relative',
    maxWidth: { base: '100px', tabletPortrait: 'initial' },
    mx: 'auto',
})).withConfig({
    shouldForwardProp: prop => prop !== 'selected' &&
        prop !== 'hide' &&
        prop !== 'compact' &&
        prop !== 'customBackgroundColor' &&
        prop !== 'customBorderColor',
}) `
    border-radius: ${props => props.theme.mf.spacing(4)};
    ${props => css `
        color: ${props.theme.mf.colorPalette.n400};
    `}

    ${props => props.compact
    ? css `
                  width: 120px;
              `
    : ''}

    ${props => props.selected
    ? css `
                  background-color: ${props.customBackgroundColor};
                  border: 1px solid ${props.customBorderColor};
              `
    : ''}

    ${props => props.hide
    ? css `
                  display: none !important;
              `
    : ''}
          
    box-sizing: border-box;
`;
// eslint-disable-next-line local-rules/no-enum
var StatusState;
(function (StatusState) {
    StatusState["SignedIn"] = "SignedIn";
    StatusState["SignedOut"] = "SignedOut";
    StatusState["Sick"] = "Sick";
    StatusState["Absent"] = "Absent";
    StatusState["OnVacation"] = "OnVacation";
    StatusState["None"] = "None";
    StatusState["HasBirthday"] = "HasBirthday";
})(StatusState || (StatusState = {}));
const hasProperty = (obj, property) => {
    return property in obj;
};
const employeeHasProperty = (obj, property) => {
    return property in obj;
};
const StyledTextContainer = styled(createStack({})) `
    max-width: 100%;
    * {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
`;
const PersonButton = (props) => {
    const { linkTo, person, hide, selected, compact, onClick } = props;
    const isDataLimited = useTypedSelector(state => limitedData(state));
    const shouldShowSignedIn = useTypedSelector(state => showSignedIn(state));
    const timezone = useTypedSelector(GroupsSelectors.timezone);
    const p300 = useCustomWhitelabelColor('p300');
    const p200 = useCustomWhitelabelColor('p200');
    const hasBirthdayToday = React.useMemo(() => {
        if (hasProperty(person, 'birthday')) {
            const birthday = person.birthday;
            const today = moment();
            const birthdate = moment(birthday);
            const hasBirthday = today.month() === birthdate.month() && today.date() === birthdate.date();
            return hasBirthday;
        }
        return false;
    }, [person]);
    const handleButtonClick = React.useCallback(() => {
        if (onClick) {
            onClick(person.id);
        }
    }, [person, onClick]);
    const displayName = React.useMemo(() => {
        if (compact || !person.name.lastName) {
            return person.name.firstName;
        }
        return `${person.name.firstName} ${person.name.lastName.charAt(0)}.`;
    }, [person, compact]);
    const pickupDetails = React.useMemo(() => {
        if (hasProperty(person, 'pickupTime') &&
            hasProperty(person, 'pickupName') &&
            person.pickupTime &&
            person.pickupName &&
            timezone) {
            /** Following the timezone logic from signin-app/src/child/check-in/pickup-time.tsx */
            const pickupTime = moment(person.pickupTime).tz(timezone);
            const hours = pickupTime.hour().toString().padStart(2, '0');
            const minutes = pickupTime.minute().toString().padStart(2, '0');
            const formattedTime = `${hours}:${minutes}`;
            return `${formattedTime} • ${person.pickupName}`;
        }
        return '';
    }, [person, timezone]);
    const personButtonInner = React.useMemo(() => (_jsxs(StyledPersonContainer, { hide: hide, selected: selected, compact: compact, customBackgroundColor: p200, customBorderColor: p300, children: [_jsx(PersonAvatar, { person: person, isDataLimited: isDataLimited, hasBirthdayToday: hasBirthdayToday }), _jsx(StyledTextContainer, { mt: compact ? 3 : 2, children: _jsx(Text, { variant: compact ? 'body' : 'h5', emphasized: true, children: displayName }) }), shouldShowSignedIn && _jsx(Text, { variant: "body-small", children: pickupDetails })] })), [
        person,
        displayName,
        hide,
        selected,
        hasBirthdayToday,
        isDataLimited,
        shouldShowSignedIn,
        pickupDetails,
        compact,
        p300,
        p200,
    ]);
    return (_jsx(_Fragment, { children: linkTo ? (_jsx(Link, { to: linkTo, onClick: handleButtonClick, "data-e2e-class": "signin-person-button", children: _jsx(Box, { children: personButtonInner }) })) : (_jsx(Box, { onClick: handleButtonClick, "data-e2e-class": "signin-person-button", children: personButtonInner })) }));
};
const PersonAvatar = ({ person, isDataLimited, hasBirthdayToday, }) => {
    const { isTabletPortraitAndLarger } = useBreakpoints();
    const initials = React.useMemo(() => {
        var _a, _b, _c, _d;
        return `${(_b = (_a = person.name.firstName) === null || _a === void 0 ? void 0 : _a.charAt(0)) !== null && _b !== void 0 ? _b : ''}${(_d = (_c = person.name.lastName) === null || _c === void 0 ? void 0 : _c.charAt(0)) !== null && _d !== void 0 ? _d : ''}`;
    }, [person]);
    const wentHome = React.useMemo(() => {
        if (hasProperty(person, 'checkins')) {
            return getWentHome(person.checkins);
        }
        if (employeeHasProperty(person, 'checkoutTime')) {
            return !person.checkedIn && person.checkoutTime;
        }
        return false;
    }, [person]);
    const statuses = React.useMemo(() => {
        const statuses = [];
        if (wentHome) {
            statuses.push(StatusState.SignedOut);
        }
        if (person.checkedIn) {
            statuses.push(StatusState.SignedIn);
        }
        if (hasProperty(person, 'hasVacation') && person.hasVacation) {
            statuses.push(StatusState.OnVacation);
        }
        if (hasProperty(person, 'isSick') && person.isSick) {
            statuses.push(StatusState.Sick);
        }
        if (hasProperty(person, 'isAbsent') && person.isAbsent) {
            statuses.push(StatusState.Absent);
        }
        if (hasBirthdayToday) {
            statuses.push(StatusState.HasBirthday);
        }
        return statuses;
    }, [person, wentHome, hasBirthdayToday]);
    return (
    // Bottom margin is handled by the element below the image
    _jsxs(Stack, { position: "relative", mx: { base: 0, tabletPortrait: 4 }, mt: { base: 0, tabletPortrait: 2 }, children: [_jsx(PersonImage, { image: person.image.small, initials: initials, hasOverlay: !isDataLimited ? !statuses.includes(StatusState.SignedIn) : false, size: isTabletPortraitAndLarger ? 'xl' : 'lg' }), !isDataLimited && _jsx(StatusBadges, { statuses: statuses, hasBirthdayToday: hasBirthdayToday })] }));
};
const BirthdayBadge = ({ index }) => {
    return (_jsx(Badge, { zIndex: 1, index: index, backgroundColor: "n0", borderColor: "n75", children: _jsx(Text, { variant: "h5", children: "\uD83C\uDF82" }) }));
};
const StatusBadges = ({ statuses, hasBirthdayToday }) => {
    const getProperties = React.useCallback((status) => {
        switch (status) {
            case StatusState.SignedIn:
                return { backgroundColor: 'g400', foregroundColor: 'n0', borderColor: 'n0', icon: 'done' };
            case StatusState.SignedOut:
                return { backgroundColor: 'n50', foregroundColor: 'n300', borderColor: 'n100', icon: 'logout' };
            case StatusState.OnVacation:
                return { backgroundColor: 'y400', foregroundColor: 'n0', borderColor: 'n0', icon: 'light_mode' };
            case StatusState.Sick:
                return { backgroundColor: 'r400', foregroundColor: 'n0', borderColor: 'n0', icon: 'device_thermostat' };
            case StatusState.Absent:
                return { backgroundColor: 'b400', foregroundColor: 'n0', borderColor: 'n0', icon: 'event_busy' };
            default:
                return undefined;
        }
    }, []);
    if ((!statuses || statuses.length === 0) && !hasBirthdayToday) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [statuses === null || statuses === void 0 ? void 0 : statuses.map((status, index) => {
                const properties = getProperties(status);
                if (!(properties === null || properties === void 0 ? void 0 : properties.backgroundColor) ||
                    !properties.borderColor ||
                    !properties.icon ||
                    !properties.foregroundColor) {
                    return null;
                }
                return (_jsx(Badge, { backgroundColor: properties.backgroundColor, borderColor: properties.borderColor, index: index, children: _jsx(Icon, { name: properties.icon, size: 32, color: properties.foregroundColor }) }, properties.icon));
            }), hasBirthdayToday ? _jsx(BirthdayBadge, { index: statuses.length >= 0 ? statuses.length - 1 : 0 }) : null] }));
};
export default PersonButton;
